import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Divider,
  Paper,
  Stack,
  Chip,
  Button,
  useMediaQuery,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import TipsAndUpdatesRoundedIcon from "@mui/icons-material/TipsAndUpdatesRounded";
import HeadphonesRoundedIcon from "@mui/icons-material/HeadphonesRounded";
import MenuBookRoundedIcon from "@mui/icons-material/MenuBookRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";
import CompressRoundedIcon from "@mui/icons-material/CompressRounded";
import LibraryMusicRoundedIcon from "@mui/icons-material/LibraryMusicRounded";
import SpeedRoundedIcon from "@mui/icons-material/SpeedRounded";
import BoltRoundedIcon from "@mui/icons-material/BoltRounded";
import { Link as RouterLink } from "react-router-dom";
import { useStyle } from "../../context/styleContext/styleContext";
import Seo from "../../components/Seo";
import ArticleSchemaMarkup from "./ArticleSchemaMarkup.jsx";

export default function PunchyDrumsLearning() {
  const { style } = useStyle();
  const isMobile = useMediaQuery("(max-width:768px)");
  const [height, setHeight] = useState(window.innerHeight);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "auto" });
  }, []);

  useEffect(() => {
    function handleResize() {
      setHeight(window.innerHeight);
    }

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const accent = "rgb(88, 166, 124)";

  const quickFacts = [
    {
      title: "What punch means",
      text: "Punch comes from clear attack, controlled sustain, solid low-end weight, and enough space for the drum hit to speak.",
      icon: <BoltRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "What kills punch",
      text: "Too much low-mid buildup, over-compression, slow transients, phase problems, and drums fighting the bass or guitars.",
      icon: <HeadphonesRoundedIcon sx={{ color: accent }} />,
    },
    {
      title: "Best first move",
      text: "Balance the close mics, overheads, room, and bass relationship before adding heavy processing.",
      icon: <TuneRoundedIcon sx={{ color: accent }} />,
    },
  ];

  const commonProblems = [
    {
      title: "The kick has weight but no impact",
      description:
        "This usually means the low end is there, but the attack is buried. The drum may need more beater definition, less low-mid cloud, or better space around it.",
    },
    {
      title: "The snare sounds loud but not punchy",
      description:
        "A snare can be loud and still feel flat. If the transient is softened or the sustain is uncontrolled, it may not hit with authority.",
    },
    {
      title: "The drums feel small in the full mix",
      description:
        "Drums can sound huge in solo and disappear once guitars, bass, synths, and vocals come in. Punch is judged in context, not in isolation.",
    },
    {
      title: "Compression makes the drums weaker",
      description:
        "Too much gain reduction or the wrong attack/release settings can flatten the hit instead of making it more exciting.",
    },
  ];

  const steps = [
    {
      title: "Start with the drum balance",
      description:
        "Before adding EQ or compression, listen to the relationship between kick, snare, toms, overheads, room mics, and any samples. Punch often starts with the right balance. If the room mics are washing over the close mics, or the overheads are too bright and thin, the kit can lose impact before processing even begins.",
    },
    {
      title: "Check phase and timing",
      description:
        "Phase issues can quietly remove the body and punch from drums. Kick in, kick out, snare top, snare bottom, overheads, and room mics all need to work together. If the low end gets smaller when you add a mic, check polarity and timing before reaching for more EQ.",
    },
    {
      title: "Give the kick a clear role",
      description:
        "A punchy kick usually needs both low-end weight and attack. The low end gives size, while the upper attack helps the kick read on smaller speakers. Decide whether the kick or bass owns the deepest space so they do not blur together.",
    },
    {
      title: "Shape the snare attack",
      description:
        "Snare punch comes from the initial hit, the body, and the decay working together. If the attack is too soft, the snare can feel behind the track. If the body is too thick, it can feel boxy. If the decay rings too long, it can clutter the groove.",
    },
    {
      title: "Control low-mid buildup",
      description:
        "Drums can get muddy fast around the low mids, especially with toms, rooms, overheads, and snare body. Clean up the buildup carefully so the kit keeps weight without losing definition.",
    },
    {
      title: "Use transient shaping when compression is not enough",
      description:
        "A transient shaper can bring out attack or tighten sustain without reacting like a compressor. It is useful when you want the hit to speak more clearly without changing the whole groove.",
    },
    {
      title: "Compress for movement, not just loudness",
      description:
        "Compression can make drums feel more controlled and exciting, but the settings matter. A slower attack can let the transient through, while a faster attack can clamp down and smooth the hit. Release time controls how the groove breathes.",
    },
    {
      title: "Use parallel processing for power",
      description:
        "Parallel compression, saturation, or clipping can add density while preserving the natural drum hit. Blend the processed signal under the dry drums until the kit feels stronger without sounding crushed.",
    },
    {
      title: "Finish in the full mix",
      description:
        "A drum sound that feels punchy in solo may be too loud, too bright, or too roomy in the full track. Always make the final decisions with the bass, guitars, vocals, and main instruments playing.",
    },
  ];

  const frequencyAreas = [
    {
      range: "50–80 Hz",
      title: "Kick weight",
      description:
        "Adds size and depth to the kick. Too much can eat headroom or fight the bass.",
    },
    {
      range: "100–200 Hz",
      title: "Drum body",
      description:
        "Important for weight in kick, snare, and toms. Too much can make the kit feel thick or slow.",
    },
    {
      range: "250–500 Hz",
      title: "Mud and boxiness",
      description:
        "A common area for cloudy drums, especially with rooms, toms, and snare body.",
    },
    {
      range: "2–5 kHz",
      title: "Attack and crack",
      description:
        "Helps kick and snare cut through the mix. Too much can become sharp or clicky.",
    },
    {
      range: "8–12 kHz",
      title: "Cymbal detail and air",
      description:
        "Adds brightness and openness, but too much can make the kit harsh or distracting.",
    },
  ];

  const processingTools = [
    {
      title: "EQ",
      description:
        "Clears low-mid buildup, shapes body, and brings out attack where the drums need more definition.",
    },
    {
      title: "Compression",
      description:
        "Controls peaks, adds density, and changes how the drums move with the groove.",
    },
    {
      title: "Transient shaping",
      description:
        "Adjusts attack and sustain directly, making drums hit harder or tighten up without relying only on compression.",
    },
    {
      title: "Saturation",
      description:
        "Adds harmonic density so drums feel more present and energetic without only turning them up.",
    },
    {
      title: "Clipping",
      description:
        "Can shave peaks and increase perceived punch or loudness when used carefully, especially on drum buses.",
    },
  ];

  const bestPractices = [
    "Do not judge drum punch in solo. Make decisions with the bass and main instruments playing.",
    "Check phase before using heavy EQ or compression.",
    "Use slower compressor attack settings when you want more transient impact.",
    "Use transient shaping when you need more attack or less sustain without changing the whole tone.",
    "Clean low mids carefully so the drums stay clear without losing body.",
    "Parallel processing can add power while keeping the original hit intact.",
    "Avoid making cymbals louder when the real problem is kick or snare definition.",
  ];

  const relatedLinks = [
    { label: "Compression", to: "/learning/compression" },
    { label: "Transient Shaper", to: "/learning/transient-shaper" },
    { label: "Parallel Processing", to: "/learning/parallel-processing" },
    { label: "Clipping vs Limiting", to: "/learning/clipping-vs-limiting" },
    { label: "Saturation", to: "/learning/saturation" },
    { label: "How to Fix a Muddy Mix", to: "/learning/fix-muddy-mix" },
  ];

  const outerPanelSx = {
    width: "100%",
    minWidth: 0,
    borderRadius: "20px",
    overflow: "hidden",
    backgroundColor: style.color1,
    border: `1px solid ${style.color2}`,
    boxSizing: "border-box",
  };

  const subtlePanelSx = {
    borderRadius: "14px",
    backgroundColor: style.darkerPanel,
    border: "1px solid rgba(255,255,255,0.06)",
    boxShadow: "none",
  };

  return (
    <>
      <Seo
        title="How to Get Punchy Drums | Parish Audio Learning"
        description="Learn how to get punchy drums with better balance, phase, EQ, compression, transient shaping, saturation, clipping, and parallel processing."
        canonical="https://parishaudio.com/learning/how-to-get-punchy-drums"
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        imageAlt="Recording studio background for a Parish Audio article about getting punchy drums"
      />

      <ArticleSchemaMarkup
        canonical="https://parishaudio.com/learning/how-to-get-punchy-drums"
        headline="How to Get Punchy Drums"
        description="Learn how to get punchy drums with better balance, phase, EQ, compression, transient shaping, saturation, clipping, and parallel processing."
        image="https://parishaudio.com/RecordingStudioStockImage1.jpg"
        articleSection="Audio Education"
        keywords={[
          "how to get punchy drums",
          "punchy drums",
          "drum mixing",
          "drum compression",
          "transient shaping",
          "parallel compression",
          "drum EQ",
          "music production",
        ]}
        breadcrumbs={[
          { name: "Home", item: "https://parishaudio.com/" },
          { name: "Learning", item: "https://parishaudio.com/learning" },
          {
            name: "How to Get Punchy Drums",
            item: "https://parishaudio.com/learning/how-to-get-punchy-drums",
          },
        ]}
        datePublished="2026-05-02T00:00:00-06:00"
        dateModified="2026-05-02T00:00:00-06:00"
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: "100vw",
          overflowX: "hidden",
          backgroundColor: "black",
          px: { xs: 1.25, sm: 1.5, md: 2.5 },
          py: { xs: 1.5, md: 2.5 },
          boxSizing: "border-box",
          minHeight: height - 130,
        }}
      >
        <Box sx={{ width: "100%", maxWidth: 1120, mx: "auto", minWidth: 0 }}>
          <Stack spacing={{ xs: 2, md: 2.5 }}>
            <Paper
              elevation={0}
              sx={{
                ...outerPanelSx,
                minHeight: { xs: 280, md: 340 },
                display: "flex",
                alignItems: "center",
                backgroundImage:
                  'linear-gradient(90deg, rgba(8,12,18,0.86) 0%, rgba(8,12,18,0.75) 38%, rgba(8,12,18,0.46) 68%, rgba(8,12,18,0.22) 100%), url("/RecordingStudioStockImage1.jpg")',
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            >
              <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: "100%" }}>
                <Box sx={{ maxWidth: 820 }}>
                  <Stack spacing={1.25} alignItems="flex-start">
                    <Chip
                      label="Drum Mixing Guide"
                      sx={{
                        borderRadius: "5px",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.08)",
                        border: "1px solid rgba(255,255,255,0.10)",
                      }}
                    />

                    <Typography
                      variant="h1"
                      sx={{
                        fontSize: { xs: "2.15rem", md: "3.4rem" },
                        fontWeight: 800,
                        lineHeight: 1.02,
                        color: style.color4,
                        letterSpacing: "-0.04em",
                      }}
                    >
                      How to Get Punchy Drums
                    </Typography>

                    <Typography
                      sx={{
                        color: "rgba(255,255,255,0.86)",
                        fontSize: { xs: "1rem", md: "1.1rem" },
                        lineHeight: 1.75,
                        maxWidth: 740,
                      }}
                    >
                      Punchy drums do not come from one magic setting. They come from a
                      drum sound that has clear attack, controlled sustain, enough weight,
                      and room to move inside the mix. The goal is not just louder drums.
                      The goal is drums that hit with purpose and stay defined when the
                      whole track is playing.
                    </Typography>

                    <Typography sx={{ color: style.color3, fontSize: "0.9rem" }}>
                      Published May 2, 2026
                    </Typography>
                  </Stack>
                </Box>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
                gap: 2,
                alignItems: "stretch",
              }}
            >
              {quickFacts.map((item) => (
                <Paper key={item.title} elevation={0} sx={{ ...outerPanelSx }}>
                  <Box sx={{ p: 2 }}>
                    <Stack direction="row" spacing={1.25} alignItems="flex-start">
                      {item.icon}
                      <Box sx={{ minWidth: 0 }}>
                        <Typography
                          sx={{
                            fontWeight: 700,
                            color: style.color4,
                            mb: 0.5,
                            lineHeight: 1.2,
                          }}
                        >
                          {item.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ color: style.color3, lineHeight: 1.55 }}
                        >
                          {item.text}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      What Makes Drums Feel Punchy?
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Drum punch is the feeling of a hit arriving clearly and moving the
                    track forward. It is the snap of the snare, the push of the kick, the
                    crack of the transient, and the way the drum returns to space before
                    the next hit. A punchy drum sound feels controlled, not crowded.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    More volume alone rarely solves it. If the transient is dull, the low
                    mids are cloudy, the phase is weak, or the compression is flattening
                    the hit, turning the drums up only makes the problem louder. Punch
                    comes from shape, timing, balance, and space.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <AutoAwesomeRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Common Drum Punch Problems
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "repeat(2, minmax(0, 1fr))" },
                      gap: 1.25,
                    }}
                  >
                    {commonProblems.map((problem) => (
                      <Paper key={problem.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                        <Typography sx={{ fontWeight: 800, color: style.color4, mb: 0.5 }}>
                          {problem.title}
                        </Typography>
                        <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                          {problem.description}
                        </Typography>
                      </Paper>
                    ))}
                  </Box>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TuneRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Step-by-Step: Building Punchy Drums
                      </Typography>
                    </Stack>

                    <Stack spacing={1.2}>
                      {steps.map((step, index) => (
                        <Paper key={step.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Stack spacing={0.6}>
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {index + 1}. {step.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {step.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <GraphicEqRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Useful Drum Frequency Areas
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      These ranges are starting points. The right move depends on the kit,
                      arrangement, tempo, and style.
                    </Typography>

                    <Stack spacing={1.2}>
                      {frequencyAreas.map((area) => (
                        <Paper key={area.range} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Stack spacing={0.7}>
                            <Chip
                              label={area.range}
                              size="small"
                              sx={{
                                alignSelf: "flex-start",
                                borderRadius: "5px",
                                color: style.color4,
                                backgroundColor: "rgba(255,255,255,0.06)",
                                border: "1px solid rgba(255,255,255,0.08)",
                                fontWeight: 700,
                              }}
                            />
                            <Typography sx={{ fontWeight: 800, color: style.color4 }}>
                              {area.title}
                            </Typography>
                            <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                              {area.description}
                            </Typography>
                          </Stack>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CompressRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Compression Can Add Punch or Remove It
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Compression is one of the most common drum tools, but it is also one
                    of the easiest ways to lose punch. If the attack is too fast, the
                    compressor can shave off the front of the hit. If the release is too
                    slow, the compressor may not recover before the next drum arrives.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    For more impact, try letting some transient through before the
                    compressor grabs the signal. Then adjust release so the gain reduction
                    moves with the groove. The goal is not just control. The goal is motion,
                    shape, and energy.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    If compression is changing too much of the drum tone, a transient
                    shaper may be a better first move. It can increase attack or reduce
                    sustain more directly, which is often exactly what a drum track needs.
                  </Typography>
                </Stack>
              </Box>
            </Paper>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                gap: 2.25,
                alignItems: "stretch",
              }}
            >
              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <SpeedRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Processing Tools for Punch
                      </Typography>
                    </Stack>

                    <Typography sx={{ color: style.color3, lineHeight: 1.75 }}>
                      Punch usually comes from a combination of tools, not one processor
                      doing everything.
                    </Typography>

                    <Stack spacing={1.2}>
                      {processingTools.map((tool) => (
                        <Paper key={tool.title} elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                          <Typography sx={{ fontWeight: 800, color: style.color4, mb: 0.4 }}>
                            {tool.title}
                          </Typography>
                          <Typography variant="body2" sx={{ color: style.color3, lineHeight: 1.65 }}>
                            {tool.description}
                          </Typography>
                        </Paper>
                      ))}
                    </Stack>
                  </Stack>
                </Box>
              </Paper>

              <Paper elevation={0} sx={{ ...outerPanelSx }}>
                <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                  <Stack spacing={1.5}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <TipsAndUpdatesRoundedIcon sx={{ color: accent }} />
                      <Typography
                        sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                      >
                        Best Practices
                      </Typography>
                    </Stack>

                    <Box
                      component="ul"
                      sx={{
                        pl: 2.5,
                        m: 0,
                        color: style.color3,
                        "& li": { mb: 1.25, lineHeight: 1.75 },
                      }}
                    >
                      {bestPractices.map((tip, index) => (
                        <li key={index}>{tip}</li>
                      ))}
                    </Box>

                    <Divider sx={{ backgroundColor: "rgba(255,255,255,0.08)" }} />

                    <Paper elevation={0} sx={{ ...subtlePanelSx, p: 1.5 }}>
                      <Stack spacing={1}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <HeadphonesRoundedIcon sx={{ color: accent }} />
                          <Typography sx={{ color: style.color4, fontWeight: 800 }}>
                            Quick listening test
                          </Typography>
                        </Stack>

                        <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                          Turn the mix down low and listen to the groove. If the drums
                          still move the song forward without feeling harsh or oversized,
                          you are closer to real punch.
                        </Typography>
                      </Stack>
                    </Paper>
                  </Stack>
                </Box>
              </Paper>
            </Box>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <LibraryMusicRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.35rem", fontWeight: 800, color: style.color4 }}
                    >
                      Tools That Help
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    If the drum hit needs more attack, a transient shaper is one of the
                    most direct tools for the job. It can bring the front of the hit forward
                    or tighten the sustain without having to rebuild the entire drum chain.
                  </Typography>

                  <Typography sx={{ color: style.color3, lineHeight: 1.8 }}>
                    Saturation and clipping can also help drums feel more solid and present.
                    Used carefully, they add density and shave peaks in a way that can make
                    drums feel louder and more controlled without simply turning them up.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Button
                      component={RouterLink}
                      to="/products/striker"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: style.accentGreen,
                        "&:hover": {
                          backgroundColor: style.accentGreen,
                          filter: "brightness(1.05)",
                        },
                      }}
                    >
                      View Striker
                    </Button>

                    <Button
                      component={RouterLink}
                      to="/products/peakline"
                      sx={{
                        borderRadius: "5px",
                        textTransform: "none",
                        fontWeight: 700,
                        color: style.color4,
                        backgroundColor: "rgba(255,255,255,0.05)",
                        border: `1px solid ${accent}`,
                        "&:hover": {
                          borderColor: style.color4,
                          backgroundColor: "rgba(255,255,255,0.10)",
                        },
                      }}
                    >
                      View Peakline
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </Paper>

            <Paper elevation={0} sx={outerPanelSx}>
              <Box sx={{ p: { xs: 1.5, md: 2 } }}>
                <Stack spacing={1.5}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <MenuBookRoundedIcon sx={{ color: accent }} />
                    <Typography
                      sx={{ fontSize: "1.25rem", fontWeight: 800, color: style.color4 }}
                    >
                      Related Topics
                    </Typography>
                  </Stack>

                  <Typography sx={{ color: style.color3, lineHeight: 1.7 }}>
                    These articles connect directly to drum punch, dynamics, saturation,
                    and mix clarity.
                  </Typography>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {relatedLinks.map((link) => (
                      <Button
                        key={link.to}
                        component={RouterLink}
                        to={link.to}
                        sx={{
                          borderRadius: "5px",
                          textTransform: "none",
                          fontWeight: 700,
                          color: style.color4,
                          backgroundColor: "rgba(255,255,255,0.05)",
                          border: `1px solid ${accent}`,
                          "&:hover": {
                            borderColor: style.color4,
                            backgroundColor: "rgba(255,255,255,0.10)",
                          },
                        }}
                      >
                        {link.label}
                      </Button>
                    ))}
                  </Stack>
                </Stack>
              </Box>
            </Paper>
          </Stack>
        </Box>
      </Box>
    </>
  );
}